import enCaVoidChequeImage from '@assets/images/ca-void-cheque-en.svg';
import frCaVoidChequeImage from '@assets/images/ca-void-cheque-fr.svg';
import usEnVoidChequeImage from '@assets/images/us-void-cheque-en.svg';
import { FeatureFlagTypes } from '@generated/graphql';
import type { PhysicalAddress } from '@interfaces/physicalAddress';
import type { Currencies } from '@interfaces/transaction';

import { CountryCodes, Languages } from '@onevesthq/ov-enums';
import { AddressField } from '@ovComponents/2-component/addressField/addressField';
import { DroppableFileInput } from '@ovComponents/2-component/droppableFileInput/droppableFileInput';
import { Form } from '@ovComponents/2-component/form/form';

import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { getCountryName } from '@ovComponents/resources';
import {
  UserContext,
  useOrganizationFeatureFlag,
} from '@providers/userContextProvider';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from 'hephaestus/web';
import { Close as CloseIcon } from 'hephaestus/webIcons';
import {
  type Dispatch,
  type SetStateAction,
  useContext,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { findIntegrationCondition } from '../findIntegrationCondition';
import type { InputProp } from './addBankAccountManually';

const styles = {
  input: {
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
    '& input[type=number]::-webkit-outer-spin-button': {
      WebkitAppearance: 'none',
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
    },
    marginBottom: '32px',
  },
};

const AddBankAccountManuallyVisual = ({
  loading,
  handleClose,
  onCreateBank,
  voidChequeFile,
  setVoidChequeFile,
  bankAccountName,
  setBankAccountName,
  institutionNumber,
  setInstitutionNumber,
  transitNumber,
  setTransitNumber,
  bankAccount,
  setBankAccount,
  onNext,
  options,
  bankAccountTransitPhysicalAddress,
  setBankAccountTransitPhysicalAddress,
  bankAccountHolderName,
  setBankAccountHolderName,
  currency,
  setCurrency,
  allowedCountryCodes,
  routingNumber,
  setRoutingNumber,
}: {
  voidChequeFile?: File;
  options?: any;
  setVoidChequeFile: Dispatch<SetStateAction<File | undefined>>;
  bankAccountName: InputProp;
  setBankAccountName: Dispatch<SetStateAction<InputProp>>;
  institutionNumber: InputProp;
  setInstitutionNumber: Dispatch<SetStateAction<InputProp>>;
  transitNumber: InputProp;
  setTransitNumber: Dispatch<SetStateAction<InputProp>>;
  bankAccount: InputProp;
  setBankAccount: Dispatch<SetStateAction<InputProp>>;
  currency: InputProp;
  setCurrency: Dispatch<SetStateAction<InputProp>>;
  loading: boolean;
  open: boolean;
  bankAccountHolderName: InputProp;
  setBankAccountHolderName: Dispatch<SetStateAction<InputProp>>;
  bankAccountTransitPhysicalAddress: PhysicalAddress;
  setBankAccountTransitPhysicalAddress: Dispatch<
    SetStateAction<PhysicalAddress>
  >;
  handleClose: () => void;
  onNext: (index: number) => void;
  onCreateBank: (
    bankInfo: {
      bankAccountName: string;
      institutionNumber?: string;
      transitNumber?: string;
      bankAccountNumber: string;
      bankAccountHolderName: string;
      bankAccountTransitPhysicalAddress: PhysicalAddress;
      currency: Currencies;
      routingNumber?: string;
    },
    file?: File,
  ) => void;
  allowedCountryCodes?: CountryCodes[];
  routingNumber: InputProp;
  setRoutingNumber: Dispatch<SetStateAction<InputProp>>;
}) => {
  const { t } = useTranslation(['components']);
  const theme = useTheme();
  const { availableCurrencies, activeLanguage } = useContext(UserContext);
  const [fileHasError, setFileHasError] = useState(false);
  const { activeOrganization, integrations } = useContext(UserContext);
  const hasAutomatedBankAccountLinking = useOrganizationFeatureFlag(
    FeatureFlagTypes.AutomatedBankAccountLinking,
  );

  const bankingIntegration = integrations?.find(
    findIntegrationCondition(options),
  );
  const enableAutoBanking: boolean =
    (hasAutomatedBankAccountLinking || options?.automatedBankAccountLinking) &&
    bankingIntegration;
  const [restrictedCountry, setRestrictedCountry] = useState<CountryCodes>(
    (allowedCountryCodes ?? [CountryCodes.CA])[0],
  );
  const isNonUSCountry = restrictedCountry !== CountryCodes.US;
  const validateInstitutionNumber = (value?: string) => {
    setRoutingNumber({ value: '', hasError: false, message: '' });
    let isValid = true;
    const number = value ?? institutionNumber.value;
    if (number.length === 0 || number.length !== 3) {
      isValid = false;
      const errorMessage =
        number.length === 0
          ? t(
              'components:bankAccountSelect.addBankModal.inputErrors.institutionNumberCannotBeEmpty',
            )
          : t(
              'components:bankAccountSelect.addBankModal.inputErrors.institutionNumberLength',
            );
      setInstitutionNumber({
        value: value ?? '',
        hasError: true,
        message: errorMessage,
      });
    } else {
      setInstitutionNumber({ value: value!, hasError: false, message: '' });
    }
    return isValid;
  };

  const validateRoutingNumber = (value?: string) => {
    setTransitNumber({ value: '', hasError: false, message: '' });
    setInstitutionNumber({ value: '', hasError: false, message: '' });
    let isValid = true;
    const number = value ?? routingNumber.value;
    if (number.length === 0 || number.length !== 9) {
      isValid = false;
      const errorMessage =
        number.length === 0
          ? t(
              'components:bankAccountSelect.addBankModal.inputErrors.routingNumberCannotBeEmpty',
            )
          : t(
              'components:bankAccountSelect.addBankModal.inputErrors.routingNumberLength',
            );
      setRoutingNumber({
        value: value ?? '',
        hasError: true,
        message: errorMessage,
      });
    } else {
      setRoutingNumber({ value: value!, hasError: false, message: '' });
    }
    return isValid;
  };

  const validateTransitNumber = (value?: string) => {
    setRoutingNumber({ value: '', hasError: false, message: '' });
    let isValid = true;
    const number = value ?? transitNumber.value;
    if (number.length === 0 || number.length !== 5) {
      isValid = false;
      const errorMessage =
        number.length === 0
          ? t(
              'components:bankAccountSelect.addBankModal.inputErrors.transitNumberCannotBeEmpty',
            )
          : t(
              'components:bankAccountSelect.addBankModal.inputErrors.transitNumberLength',
            );
      setTransitNumber({
        value: value ?? '',
        hasError: true,
        message: errorMessage,
      });
    } else {
      setTransitNumber({ value: value!, hasError: false, message: '' });
    }
    return isValid;
  };

  const validateBankAccountNumber = (value?: string) => {
    let isValid = true;
    const number = value ?? bankAccount.value;
    if (number.length === 0 || number.length < 5 || number.length > 17) {
      isValid = false;
      const errorMessage =
        bankAccount.value.length === 0
          ? t(
              'components:bankAccountSelect.addBankModal.inputErrors.bankAccountNumberCannotBeEmpty',
            )
          : t(
              'components:bankAccountSelect.addBankModal.inputErrors.bankAccountNumberRange',
            );
      setBankAccount({
        value: value ?? '',
        hasError: true,
        message: errorMessage,
      });
    } else {
      setBankAccount({ value: value!, hasError: false, message: '' });
    }
    return isValid;
  };

  const validateUSBankAccountNumber = (value?: string) => {
    let isValid = true;
    const number = value ?? bankAccount.value;
    if (number.length === 0 || number.length < 8 || number.length > 12) {
      isValid = false;
      const errorMessage =
        bankAccount.value.length === 0
          ? t(
              'components:bankAccountSelect.addBankModal.inputErrors.bankAccountNumberCannotBeEmpty',
            )
          : t(
              'components:bankAccountSelect.addBankModal.inputErrors.usBankAccountNumberRange',
            );
      setBankAccount({
        value: value ?? '',
        hasError: true,
        message: errorMessage,
      });
    } else {
      setBankAccount({ value: value!, hasError: false, message: '' });
    }
    return isValid;
  };

  const validateBankAccountName = (value?: string) => {
    const nameToValidate = value ?? bankAccountName.value;
    const isEmpty = nameToValidate.trim() === '';

    if (isEmpty) {
      const errorMessage = t(
        'components:bankAccountSelect.addBankModal.inputErrors.bankAccountNameCannotBeEmpty',
      );
      setBankAccountName({
        value: value ?? '',
        hasError: true,
        message: errorMessage,
      });
      return false;
    }

    setBankAccountName({
      value: nameToValidate,
      hasError: false,
      message: '',
    });
    return true;
  };

  const areFieldsValid = (): boolean => {
    let isValid = true;

    if (isNonUSCountry && !validateInstitutionNumber(institutionNumber.value)) {
      isValid = false;
    }
    if (isNonUSCountry && !validateTransitNumber(transitNumber.value)) {
      isValid = false;
    }
    if (!isNonUSCountry && !validateRoutingNumber(routingNumber.value)) {
      isValid = false;
    }
    if (!validateBankAccountNumber(bankAccount.value)) {
      isValid = false;
    }
    if (showFileUpload && !voidChequeFile) {
      isValid = false;
      setFileHasError(true);
    }
    if (!validateBankAccountName(bankAccountName.value)) {
      isValid = false;
    }

    return isValid;
  };

  const createBankAccount = () => {
    if (!areFieldsValid()) return;
    onCreateBank(
      {
        bankAccountName: bankAccountName.value,
        institutionNumber: institutionNumber.value ?? undefined,
        transitNumber: transitNumber.value ?? undefined,
        bankAccountNumber: bankAccount.value,
        bankAccountHolderName: bankAccountHolderName.value,
        bankAccountTransitPhysicalAddress,
        currency: currency.value as Currencies,
        routingNumber: routingNumber.value ?? undefined,
      },
      voidChequeFile,
    );
  };
  const showFileUpload = options ? !!options.allowDocumentUpload : true;

  return (
    <Form onSubmit={() => createBankAccount()}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: 2.5,
        }}
      >
        <Typography variant='headingLarge'>
          {t('components:bankAccountSelect.addBankManuallyModal.title')}
        </Typography>
        <IconButton aria-label='close' size='xsmall' onClick={handleClose}>
          <CloseIcon fontSize='inherit' />
        </IconButton>
      </Box>
      <Divider sx={{ marginBottom: 2.5 }} />
      <Box sx={{ px: 2.5, mb: 4 }}>
        <Typography
          variant='bodyLarge'
          sx={{ marginBottom: '32px', color: theme.color.onSurfaceVariant }}
        >
          {t('components:bankAccountSelect.addBankManuallyModal.subTitle')}
        </Typography>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '32px',
          }}
        >
          <img
            src={
              !isNonUSCountry
                ? usEnVoidChequeImage
                : activeLanguage === Languages.FRENCH
                  ? frCaVoidChequeImage
                  : enCaVoidChequeImage
            }
            alt='void-cheque'
          />
        </div>
        {(allowedCountryCodes ?? []).length > 1 &&
          allowedCountryCodes?.includes(CountryCodes.US) && (
            <Grid container spacing={2}>
              <Grid
                size={12}
                sx={{
                  mb: 2,
                }}
              >
                <RadioGroup
                  testId='bank-country-radio-group'
                  value={restrictedCountry}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setRestrictedCountry(event?.target?.value as CountryCodes);
                  }}
                  disabled={loading}
                  label={t(
                    'components:bankAccountSelect.addBankManuallyModal.bankCountry',
                  )}
                >
                  {(allowedCountryCodes ?? [CountryCodes.CA]).map(
                    (cc: CountryCodes) => (
                      <Radio
                        testId={`google-address-radio-option-${cc.toLowerCase()}`}
                        key={cc}
                        value={cc}
                        size='small'
                        label={getCountryName(cc)}
                      />
                    ),
                  )}
                </RadioGroup>
              </Grid>
            </Grid>
          )}
        <Grid container spacing={2}>
          {isNonUSCountry && (
            <>
              <Grid size={4}>
                <TextField
                  testId='transitNumber-text-field'
                  sx={styles.input}
                  error={transitNumber.hasError}
                  value={transitNumber.value}
                  errorText={transitNumber.message}
                  label={t(
                    'components:bankAccountSelect.addBankManuallyModal.transitNumber',
                  )}
                  fullWidth
                  onChange={(e: any) => {
                    validateTransitNumber(e.target.value);
                  }}
                />
              </Grid>
              <Grid size={3}>
                <TextField
                  testId='institutionNumber-text-field'
                  sx={styles.input}
                  error={institutionNumber.hasError}
                  value={institutionNumber.value}
                  label={t(
                    'components:bankAccountSelect.addBankManuallyModal.institutionNumber',
                  )}
                  fullWidth
                  errorText={institutionNumber.message}
                  onChange={(e: any) => {
                    validateInstitutionNumber(e.target.value);
                  }}
                />
              </Grid>
            </>
          )}
          {!isNonUSCountry && (
            <>
              <Grid size={7}>
                <TextField
                  testId='routingNumber-text-field'
                  sx={styles.input}
                  error={routingNumber.hasError}
                  value={routingNumber.value}
                  errorText={routingNumber.message}
                  label={t(
                    'components:bankAccountSelect.addBankManuallyModal.routingNumber',
                  )}
                  fullWidth
                  onChange={(e: any) => {
                    validateRoutingNumber(e.target.value);
                  }}
                />
              </Grid>
            </>
          )}
          <Grid size={5}>
            <TextField
              testId='bankAccount-text-field'
              sx={styles.input}
              error={bankAccount.hasError}
              value={bankAccount.value}
              label={t(
                'components:bankAccountSelect.addBankManuallyModal.accountNumber',
              )}
              errorText={bankAccount.message}
              fullWidth
              onChange={(e: any) => {
                if (!isNonUSCountry) {
                  validateUSBankAccountNumber(e.target.value);
                } else {
                  validateBankAccountNumber(e.target.value);
                }
              }}
            />
          </Grid>
        </Grid>
        <TextField
          testId='currency-text-field'
          select
          sx={styles.input}
          label={t('components:transaction.table.currency')}
          fullWidth
          error={currency.hasError}
          value={currency.value}
          onChange={(e: any) => {
            setCurrency({
              value: e.target.value as Currencies,
              hasError: false,
              message: '',
            });
          }}
        >
          {(
            activeOrganization?.productShelfCurrencies ?? availableCurrencies
          ).map((currency: string) => (
            <MenuItem
              value={currency}
              key={currency}
              testId={`${currency}-currency-menu-item`}
            >
              {t(`components:transaction.currencies.${currency}`)}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          testId='bankAccountName-text-field'
          value={bankAccountName.value}
          error={bankAccountName.hasError}
          label={t(
            'components:bankAccountSelect.addBankManuallyModal.bankAccountName',
          )}
          sx={{ marginBottom: '32px' }}
          errorText={bankAccountName.message}
          fullWidth
          onChange={(e: any) => {
            validateBankAccountName(e.target.value);
          }}
        />

        <TextField
          testId='bankAccountHolderName-text-field'
          value={bankAccountHolderName.value}
          label={t(
            'components:bankAccountSelect.addBankManuallyModal.bankAccountHolderName',
          )}
          sx={{ marginBottom: '32px' }}
          fullWidth
          onChange={(e: any) => {
            setBankAccountHolderName({
              ...bankAccountHolderName,
              value: e.target.value,
            });
          }}
        />

        <AddressField
          testId='bankAccountTransitPhysicalAddress'
          address={bankAccountTransitPhysicalAddress}
          onChange={(data: any) => {
            setBankAccountTransitPhysicalAddress({
              city: data.city || undefined,
              country: data.country || undefined,
              houseNumber: data.houseNumber || undefined,
              postal: data.postal || undefined,
              jurisdiction: data.jurisdiction || undefined,
              streetName: data.streetName || undefined,
              unitNumber: data.unitNumber || undefined,
            });
          }}
          label={t(
            'components:bankAccountSelect.addBankManuallyModal.bankTransitAddress',
          )}
          sx={{ marginBottom: '32px' }}
        />

        {showFileUpload && (
          <DroppableFileInput
            onFileChosen={(file) => {
              setVoidChequeFile(file);
              setFileHasError(false);
            }}
            error={fileHasError}
          />
        )}
      </Box>
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        sx={{
          mb: 3,
        }}
      >
        <Button
          variant='contained'
          type='submit'
          disabled={loading}
          sx={{
            marginBottom: '8px',
            borderRadius: '50px !important',
            width: '285px !important',
          }}
          data-testid='add-bank-account-button'
        >
          {t(
            'components:bankAccountSelect.addBankManuallyModal.addBankAccount',
          )}
        </Button>
        {enableAutoBanking ? (
          <Button onClick={() => onNext(2)} variant='text'>
            {t(
              'components:bankAccountSelect.addBankManuallyModal.linkFlinksButton',
            )}
          </Button>
        ) : undefined}
      </Box>
    </Form>
  );
};

export default AddBankAccountManuallyVisual;
